import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Palette, 
  Type, 
  Image, 
  Mail,
  Phone,
  Globe,
  Save,
  Upload,
  Loader2,
  MapPin,
  FileText,
  Link as LinkIcon
} from "lucide-react";

const SuperAdminCustomize = () => {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'branding';
  
  const [settings, setSettings] = useState({
    site_name: "LearnHub",
    site_logo: null,
    primary_color: "#6d28d9",
    secondary_color: "#059669",
    accent_color: "#f97316",
    hero_title: "Your Learning Journey Starts Here",
    hero_subtitle: "Access lecture recordings, learning materials, and course content.",
    hero_image: null,
    hero_background_image: null,
    footer_text: "© 2024 LearnHub. All rights reserved.",
    footer_links: {},
    social_links: {},
    contact_email: "",
    contact_phone: "",
    contact_address: ""
  });
  
  const [emailTemplates, setEmailTemplates] = useState({
    email_header_logo: null,
    email_header_color: "#6d28d9",
    email_footer_text: "© 2024 LearnHub. All rights reserved.",
    welcome_email_subject: "Welcome to LearnHub!",
    welcome_email_body: "Thank you for joining LearnHub. We're excited to have you on board!",
    notification_email_subject: "New Notification from LearnHub",
    notification_email_template: "<h2>{{title}}</h2><p>{{message}}</p>",
    certificate_email_subject: "Congratulations! Your Certificate is Ready",
    certificate_email_body: "Congratulations on completing {{course_name}}! Your certificate is now available."
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHeroImage, setUploadingHeroImage] = useState(false);
  const [uploadingHeroBg, setUploadingHeroBg] = useState(false);
  const [uploadingEmailLogo, setUploadingEmailLogo] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, emailRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/superadmin/email-templates`, { withCredentials: true }).catch(() => ({ data: {} }))
      ]);
      setSettings(prev => ({ ...prev, ...settingsRes.data }));
      if (emailRes.data && Object.keys(emailRes.data).length > 0) {
        setEmailTemplates(prev => ({ ...prev, ...emailRes.data }));
      }
    } catch (error) {
      console.error("Failed to load settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/superadmin/settings`, settings, { withCredentials: true });
      toast.success("Site settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmailTemplates = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/superadmin/email-templates`, emailTemplates, { withCredentials: true });
      toast.success("Email templates saved!");
    } catch (error) {
      toast.error("Failed to save email templates");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/superadmin/settings/logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({ ...prev, site_logo: response.data.logo_url }));
      toast.success("Logo uploaded!");
    } catch (error) {
      toast.error("Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleHeroImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingHeroImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/superadmin/settings/hero-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({ ...prev, hero_image: response.data.hero_image_url }));
      toast.success("Hero image uploaded!");
    } catch (error) {
      toast.error("Failed to upload hero image");
    } finally {
      setUploadingHeroImage(false);
    }
  };

  const handleHeroBgUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingHeroBg(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/superadmin/settings/hero-background`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings(prev => ({ ...prev, hero_background_image: response.data.hero_background_url }));
      toast.success("Hero background uploaded!");
    } catch (error) {
      toast.error("Failed to upload background");
    } finally {
      setUploadingHeroBg(false);
    }
  };

  const handleEmailLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingEmailLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/superadmin/email-templates/header-logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setEmailTemplates(prev => ({ ...prev, email_header_logo: response.data.logo_url }));
      toast.success("Email logo uploaded!");
    } catch (error) {
      toast.error("Failed to upload email logo");
    } finally {
      setUploadingEmailLogo(false);
    }
  };

  const handleSocialLinkChange = (platform, url) => {
    setSettings(prev => ({
      ...prev,
      social_links: { ...prev.social_links, [platform]: url }
    }));
  };

  const handleFooterLinkChange = (label, url) => {
    setSettings(prev => ({
      ...prev,
      footer_links: { ...prev.footer_links, [label]: url }
    }));
  };

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${API.replace('/api', '')}${path}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="superadmin-customize">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: 'Outfit' }}>
              Platform Customization
            </h1>
            <p className="text-stone-600 mt-1">Customize every aspect of your learning portal</p>
          </div>
        </div>

        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="branding">Branding</TabsTrigger>
            <TabsTrigger value="landing">Landing Page</TabsTrigger>
            <TabsTrigger value="email">Email Templates</TabsTrigger>
            <TabsTrigger value="footer">Footer & Social</TabsTrigger>
            <TabsTrigger value="contact">Contact Info</TabsTrigger>
          </TabsList>

          {/* Branding Tab */}
          <TabsContent value="branding">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-white rounded-xl border border-stone-100">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <Type className="h-5 w-5 text-violet-700" />
                    Site Identity
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Site Name</Label>
                    <Input
                      value={settings.site_name}
                      onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                      placeholder="LearnHub"
                      className="mt-1"
                      data-testid="site-name-input"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-xl border border-stone-100">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <Image className="h-5 w-5 text-violet-700" />
                    Site Logo
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4">
                    {settings.site_logo ? (
                      <img 
                        src={getImageUrl(settings.site_logo)} 
                        alt="Logo" 
                        className="w-16 h-16 object-contain rounded-lg border"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-stone-100 rounded-lg flex items-center justify-center">
                        <Image className="h-8 w-8 text-stone-400" />
                      </div>
                    )}
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        id="logo-upload"
                      />
                      <label htmlFor="logo-upload">
                        <Button 
                          variant="outline" 
                          className="cursor-pointer"
                          disabled={uploadingLogo}
                          asChild
                        >
                          <span>
                            {uploadingLogo ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Upload className="h-4 w-4 mr-2" />
                            )}
                            Upload Logo
                          </span>
                        </Button>
                      </label>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-xl border border-stone-100 md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <Palette className="h-5 w-5 text-violet-700" />
                    Brand Colors
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div>
                      <Label>Primary Color</Label>
                      <div className="flex items-center gap-3 mt-1">
                        <input
                          type="color"
                          value={settings.primary_color}
                          onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                          className="w-12 h-12 rounded-lg cursor-pointer border-0"
                        />
                        <Input
                          value={settings.primary_color}
                          onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                          placeholder="#6d28d9"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Secondary Color</Label>
                      <div className="flex items-center gap-3 mt-1">
                        <input
                          type="color"
                          value={settings.secondary_color}
                          onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                          className="w-12 h-12 rounded-lg cursor-pointer border-0"
                        />
                        <Input
                          value={settings.secondary_color}
                          onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                          placeholder="#059669"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Accent Color</Label>
                      <div className="flex items-center gap-3 mt-1">
                        <input
                          type="color"
                          value={settings.accent_color || "#f97316"}
                          onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                          className="w-12 h-12 rounded-lg cursor-pointer border-0"
                        />
                        <Input
                          value={settings.accent_color || "#f97316"}
                          onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                          placeholder="#f97316"
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="mt-6 flex justify-end">
              <Button 
                onClick={handleSaveSettings}
                disabled={saving}
                className="bg-violet-700 hover:bg-violet-800 rounded-full"
                data-testid="save-branding-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Branding
              </Button>
            </div>
          </TabsContent>

          {/* Landing Page Tab */}
          <TabsContent value="landing">
            <div className="space-y-6">
              <Card className="bg-white rounded-xl border border-stone-100">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit' }}>Hero Section Content</CardTitle>
                  <CardDescription>Customize the main banner on your landing page</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label>Hero Title</Label>
                    <Input
                      value={settings.hero_title}
                      onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
                      placeholder="Your Learning Journey Starts Here"
                      className="mt-1"
                      data-testid="hero-title-input"
                    />
                  </div>
                  <div>
                    <Label>Hero Subtitle</Label>
                    <Textarea
                      value={settings.hero_subtitle}
                      onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
                      placeholder="Access lecture recordings, learning materials..."
                      className="mt-1"
                      rows={3}
                      data-testid="hero-subtitle-input"
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-white rounded-xl border border-stone-100">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                      <Image className="h-5 w-5 text-violet-700" />
                      Hero Image
                    </CardTitle>
                    <CardDescription>The main image displayed in the hero section</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {settings.hero_image ? (
                      <img 
                        src={getImageUrl(settings.hero_image)} 
                        alt="Hero" 
                        className="w-full h-40 object-cover rounded-lg border"
                      />
                    ) : (
                      <div className="w-full h-40 bg-stone-100 rounded-lg flex items-center justify-center">
                        <Image className="h-12 w-12 text-stone-400" />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleHeroImageUpload}
                      className="hidden"
                      id="hero-image-upload"
                    />
                    <label htmlFor="hero-image-upload">
                      <Button 
                        variant="outline" 
                        className="w-full cursor-pointer"
                        disabled={uploadingHeroImage}
                        asChild
                      >
                        <span>
                          {uploadingHeroImage ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4 mr-2" />
                          )}
                          Upload Hero Image
                        </span>
                      </Button>
                    </label>
                  </CardContent>
                </Card>

                <Card className="bg-white rounded-xl border border-stone-100">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                      <Image className="h-5 w-5 text-blue-700" />
                      Hero Background
                    </CardTitle>
                    <CardDescription>Optional background image for the hero section</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {settings.hero_background_image ? (
                      <img 
                        src={getImageUrl(settings.hero_background_image)} 
                        alt="Hero Background" 
                        className="w-full h-40 object-cover rounded-lg border"
                      />
                    ) : (
                      <div className="w-full h-40 bg-stone-100 rounded-lg flex items-center justify-center">
                        <Image className="h-12 w-12 text-stone-400" />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleHeroBgUpload}
                      className="hidden"
                      id="hero-bg-upload"
                    />
                    <label htmlFor="hero-bg-upload">
                      <Button 
                        variant="outline" 
                        className="w-full cursor-pointer"
                        disabled={uploadingHeroBg}
                        asChild
                      >
                        <span>
                          {uploadingHeroBg ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Upload className="h-4 w-4 mr-2" />
                          )}
                          Upload Background
                        </span>
                      </Button>
                    </label>
                  </CardContent>
                </Card>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <Button 
                onClick={handleSaveSettings}
                disabled={saving}
                className="bg-violet-700 hover:bg-violet-800 rounded-full"
                data-testid="save-landing-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Landing Page
              </Button>
            </div>
          </TabsContent>

          {/* Email Templates Tab */}
          <TabsContent value="email">
            <div className="space-y-6">
              <Card className="bg-white rounded-xl border border-stone-100">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <Mail className="h-5 w-5 text-violet-700" />
                    Email Header & Footer
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label>Email Header Logo</Label>
                      <div className="flex items-center gap-4 mt-2">
                        {emailTemplates.email_header_logo ? (
                          <img 
                            src={getImageUrl(emailTemplates.email_header_logo)} 
                            alt="Email Logo" 
                            className="w-16 h-16 object-contain rounded-lg border"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-stone-100 rounded-lg flex items-center justify-center">
                            <Mail className="h-8 w-8 text-stone-400" />
                          </div>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleEmailLogoUpload}
                          className="hidden"
                          id="email-logo-upload"
                        />
                        <label htmlFor="email-logo-upload">
                          <Button 
                            variant="outline" 
                            className="cursor-pointer"
                            disabled={uploadingEmailLogo}
                            asChild
                          >
                            <span>
                              {uploadingEmailLogo ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Upload className="h-4 w-4 mr-2" />
                              )}
                              Upload
                            </span>
                          </Button>
                        </label>
                      </div>
                    </div>
                    <div>
                      <Label>Header Color</Label>
                      <div className="flex items-center gap-3 mt-2">
                        <input
                          type="color"
                          value={emailTemplates.email_header_color}
                          onChange={(e) => setEmailTemplates({ ...emailTemplates, email_header_color: e.target.value })}
                          className="w-12 h-12 rounded-lg cursor-pointer border-0"
                        />
                        <Input
                          value={emailTemplates.email_header_color}
                          onChange={(e) => setEmailTemplates({ ...emailTemplates, email_header_color: e.target.value })}
                          placeholder="#6d28d9"
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label>Email Footer Text</Label>
                    <Input
                      value={emailTemplates.email_footer_text}
                      onChange={(e) => setEmailTemplates({ ...emailTemplates, email_footer_text: e.target.value })}
                      placeholder="© 2024 LearnHub. All rights reserved."
                      className="mt-1"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-xl border border-stone-100">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit' }}>Welcome Email</CardTitle>
                  <CardDescription>Sent to new users when they register</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Subject</Label>
                    <Input
                      value={emailTemplates.welcome_email_subject}
                      onChange={(e) => setEmailTemplates({ ...emailTemplates, welcome_email_subject: e.target.value })}
                      placeholder="Welcome to LearnHub!"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Body</Label>
                    <Textarea
                      value={emailTemplates.welcome_email_body}
                      onChange={(e) => setEmailTemplates({ ...emailTemplates, welcome_email_body: e.target.value })}
                      placeholder="Thank you for joining..."
                      className="mt-1"
                      rows={4}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-xl border border-stone-100">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit' }}>Notification Email</CardTitle>
                  <CardDescription>Template for notification emails. Use {"{{title}}"} and {"{{message}}"} placeholders.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Subject</Label>
                    <Input
                      value={emailTemplates.notification_email_subject}
                      onChange={(e) => setEmailTemplates({ ...emailTemplates, notification_email_subject: e.target.value })}
                      placeholder="New Notification from LearnHub"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Template (HTML)</Label>
                    <Textarea
                      value={emailTemplates.notification_email_template}
                      onChange={(e) => setEmailTemplates({ ...emailTemplates, notification_email_template: e.target.value })}
                      placeholder="<h2>{{title}}</h2><p>{{message}}</p>"
                      className="mt-1 font-mono text-sm"
                      rows={4}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-xl border border-stone-100">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit' }}>Certificate Email</CardTitle>
                  <CardDescription>Sent when a student earns a certificate. Use {"{{course_name}}"} placeholder.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Subject</Label>
                    <Input
                      value={emailTemplates.certificate_email_subject}
                      onChange={(e) => setEmailTemplates({ ...emailTemplates, certificate_email_subject: e.target.value })}
                      placeholder="Congratulations! Your Certificate is Ready"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Body</Label>
                    <Textarea
                      value={emailTemplates.certificate_email_body}
                      onChange={(e) => setEmailTemplates({ ...emailTemplates, certificate_email_body: e.target.value })}
                      placeholder="Congratulations on completing {{course_name}}!"
                      className="mt-1"
                      rows={4}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="mt-6 flex justify-end">
              <Button 
                onClick={handleSaveEmailTemplates}
                disabled={saving}
                className="bg-violet-700 hover:bg-violet-800 rounded-full"
                data-testid="save-email-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Email Templates
              </Button>
            </div>
          </TabsContent>

          {/* Footer & Social Tab */}
          <TabsContent value="footer">
            <div className="space-y-6">
              <Card className="bg-white rounded-xl border border-stone-100">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <FileText className="h-5 w-5 text-violet-700" />
                    Footer Content
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Footer Text</Label>
                    <Input
                      value={settings.footer_text}
                      onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
                      placeholder="© 2024 LearnHub. All rights reserved."
                      className="mt-1"
                      data-testid="footer-text-input"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white rounded-xl border border-stone-100">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <LinkIcon className="h-5 w-5 text-violet-700" />
                    Footer Links
                  </CardTitle>
                  <CardDescription>Add quick links to your footer</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {['Privacy Policy', 'Terms of Service', 'FAQ', 'Support'].map(label => (
                    <div key={label}>
                      <Label>{label}</Label>
                      <Input
                        value={settings.footer_links?.[label] || ""}
                        onChange={(e) => handleFooterLinkChange(label, e.target.value)}
                        placeholder={`https://yoursite.com/${label.toLowerCase().replace(' ', '-')}`}
                        className="mt-1"
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="bg-white rounded-xl border border-stone-100">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                    <Globe className="h-5 w-5 text-violet-700" />
                    Social Media Links
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {['facebook', 'twitter', 'instagram', 'linkedin', 'youtube'].map(platform => (
                    <div key={platform}>
                      <Label className="capitalize">{platform}</Label>
                      <Input
                        value={settings.social_links?.[platform] || ""}
                        onChange={(e) => handleSocialLinkChange(platform, e.target.value)}
                        placeholder={`https://${platform}.com/yourpage`}
                        className="mt-1"
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
            <div className="mt-6 flex justify-end">
              <Button 
                onClick={handleSaveSettings}
                disabled={saving}
                className="bg-violet-700 hover:bg-violet-800 rounded-full"
                data-testid="save-footer-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Footer & Social
              </Button>
            </div>
          </TabsContent>

          {/* Contact Info Tab */}
          <TabsContent value="contact">
            <Card className="bg-white rounded-xl border border-stone-100">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit' }}>Contact Information</CardTitle>
                <CardDescription>How students can reach you</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Contact Email
                  </Label>
                  <Input
                    type="email"
                    value={settings.contact_email || ""}
                    onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                    placeholder="support@learnhub.com"
                    className="mt-1"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Contact Phone
                  </Label>
                  <Input
                    value={settings.contact_phone || ""}
                    onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                    placeholder="+44 123 456 7890"
                    className="mt-1"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Address
                  </Label>
                  <Textarea
                    value={settings.contact_address || ""}
                    onChange={(e) => setSettings({ ...settings, contact_address: e.target.value })}
                    placeholder="123 Learning Street, London, UK"
                    className="mt-1"
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
            <div className="mt-6 flex justify-end">
              <Button 
                onClick={handleSaveSettings}
                disabled={saving}
                className="bg-violet-700 hover:bg-violet-800 rounded-full"
                data-testid="save-contact-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Contact Info
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default SuperAdminCustomize;
